import type { Option } from '@/models/option';

import type * as Proto from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_cancellation_policy_trigger';

export const WabunkaCancellationPolicyTriggerList = ['RESERVATION_CONFIRMED', 'CONFIRMED_BOOKING_LEAD_HOURS', 'RESERVATION_REQUESTED'] as const;
export type WabunkaCancellationPolicyTrigger = (typeof WabunkaCancellationPolicyTriggerList)[number];

export const wabunkaCancellationPolicyTriggerLabelList = ['予約確定', 'キャンセル・変更締切', '予約受付'] as const;
export type WabunkaCancellationPolicyTriggerLabel = (typeof wabunkaCancellationPolicyTriggerLabelList)[number];

/**
 * proto の型からフロントエンド用のモデルの型（union type）に変換して返却する
 */
export const buildWabunkaCancellationPolicyTrigger = (res: Proto.WabunkaCancellationPolicyTrigger): WabunkaCancellationPolicyTrigger => {
  switch (res) {
    case 1:
      return 'RESERVATION_CONFIRMED';
    case 2:
      return 'CONFIRMED_BOOKING_LEAD_HOURS';
    case 3:
      return 'RESERVATION_REQUESTED';
    default:
      throw new Error(`=== getLabel: unknown, res: ${res}`);
  }
};

/**
 * cancelPolicyTrigger を引数に取り、ラベルを返却する
 * @param {WabunkaCancellationPolicyTrigger} cancellationPolicyTrigger - 通訳
 * @returns {WabunkaCancellationPolicyTriggerLabel} - ラベル（日本語）
 */
export const getWabunkaCancellationPolicyTriggerLabel = (cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger): WabunkaCancellationPolicyTriggerLabel => {
  switch (cancellationPolicyTrigger) {
    case 'RESERVATION_CONFIRMED':
      return '予約確定';
    case 'CONFIRMED_BOOKING_LEAD_HOURS':
      return 'キャンセル・変更締切';
    case 'RESERVATION_REQUESTED':
      return '予約受付';
    default:
      throw new Error(`=== getLabel: unknown, res: ${cancellationPolicyTrigger}`);
  }
};

/**
 * CancelPolicyTrigger のセレクトボックス用の配列を返却する
 * @returns {Options} - 配列
 */
/**
 * セレクトボックス用の配列
 */
export const wabunkaCancellationPolicyTriggerOptions: Option[] = WabunkaCancellationPolicyTriggerList.map((_) => ({
  value: _,
  label: getWabunkaCancellationPolicyTriggerLabel(_),
}));

/**
 * キャンセルポリシー適用条件についてのテキストを返却する
 * @param {WabunkaCancellationPolicyTrigger} cancellationPolicyTrigger - キャンセルポリシー適用条件
 * @param {number} refundableCancellationDaysForGuest - 何日前まで払い戻し有りのキャンセルが可能か
 * @returns string
 */
export const cancelPolicyText = (cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger, refundableCancellationDaysForGuest: number): string => {
  switch (cancellationPolicyTrigger) {
    case 'RESERVATION_CONFIRMED':
      return 'No cancellations after booking has been confirmed';
    case 'CONFIRMED_BOOKING_LEAD_HOURS': {
      let cancelableDaysText = '';
      if (refundableCancellationDaysForGuest == 1) {
        cancelableDaysText = refundableCancellationDaysForGuest + ' day';
      } else {
        cancelableDaysText = refundableCancellationDaysForGuest + ' days';
      }
      return `Cancel free up to ${cancelableDaysText} prior`;
    }
    case 'RESERVATION_REQUESTED':
      return 'No cancellations after booking';
    default:
      return '';
  }
};

/**
 * キャンセルポリシー適用条件に基づいてメッセージを作成
 * - キャンセルポリシーのラダー設定が有効な場合、"Things to know" を参照する必要がある旨の文言に変える
 *
 * @param {WabunkaCancellationPolicyTrigger} cancellationPolicyTrigger - キャンセルポリシー適用条件
 * @param {number} refundableCancellationDaysForGuest - 何日前まで払い戻し有りのキャンセルが可能か
 * @param {boolean} [isLadderEnabled=false] - ラダー機能が有効かどうか
 * @returns {string} キャンセルポリシーの説明文
 */
export const buildCancelFeeMessage = (cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger, refundableCancellationDaysForGuest: number, isLadderEnabled: boolean = false): string => {
  switch (cancellationPolicyTrigger) {
    case 'RESERVATION_REQUESTED':
      if (isLadderEnabled) {
        return 'A cancellation fee applies immediately upon booking. Please refer to the Cancellation Policy in the "Things to know" section for the full fee schedule.';
      }
      return 'A 100% cancellation fee applies immediately.';
    case 'RESERVATION_CONFIRMED':
      return 'A 100% cancellation fee applies after booking has been confirmed.';
    case 'CONFIRMED_BOOKING_LEAD_HOURS': {
      let cancelableDaysText = '';
      if (refundableCancellationDaysForGuest == 1) {
        cancelableDaysText = refundableCancellationDaysForGuest + ' day';
      } else {
        cancelableDaysText = refundableCancellationDaysForGuest + ' days';
      }
      if (isLadderEnabled) {
        return `No cancellation fees up to ${cancelableDaysText} before the event (Japan time). After that, cancellation fees apply according to the Cancellation Policy in the "Things to know" section. Please check the full policy for details.`;
      }
      return `No cancellation fees up to ${cancelableDaysText} before the event (Japan time). Afterward, a 100% cancellation fee applies.`;
    }
    default:
      return '';
  }
};
